import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { BASENAME } from '../../../constants';
import Loading from '../../atoms/Loading';
import LineTitleGray from '../../atoms/titles/LineTitleGray';
import SubscriptionTable from '../../organisms/SubscriptionTable';
import LinkButton from '../../atoms/button/LinkButton';
import SubscriptionPrice from '../../molecules/SubscriptionPrice';
import NoteEntryConfirmation from '../../molecules/NoteEntryConfirmation';
import DataUpdateTimeDisplay from '../../molecules/DataUpdateTimeDisplay';
import SubscriptionPlanContents from '../../organisms/SubscriptionPlanContents';
import { LAYOUT_TYPES, SHOWING_HOTEL_LIMIT } from '../../../constants';
import { setCurrentStep, setLayoutType } from '../../../modules/app';
import {
  fetchHotels,
} from '../../../modules/hotel';
import { makeHotelsInput, makeCreateSessionInput } from '../../../utils/input';
import { useSearchFlightSync } from '../../../modules/hooks/searchFlight';
import { useWindowSize } from '../../../utils/windowSize';
import { scrollToTop } from '../../../utils/scroll';
import { getPlanFromPlansByDate } from '../../../utils/plans';
import { createSession } from '../../../modules/session';
import { usePrice } from '../../../utils/price';
import { useProgress } from '../../../modules/hooks/progress';
import PlanningInfo from '../../molecules/PlanningInfo';
import { FData } from '../../Head';

const selector = (state) => ({ planDetail: state.planDetail });

const EntryConfirmation = () => {
  const dispatch = useDispatch();
  const [ isPCView, setIsPCView ] = useState(false);
  const [ editingFlights, setEditingFlights ] = useState(false);
  const [ editingAccommodation, setEditingAccommodation ] = useState(false);
  const [ maxStayNights, setMaxStayNights ] = useState(undefined);
  const [ planStartDate, setPlanStartDate ] = useState(undefined);
  const [ planIndex, setPlanIndex ] = useState(undefined);
  const [ fetching, setFetching ] = useState(false);
  const [ isIntersectingNote, setIsIntersectingNote ] = useState(false);
  const [ isIntersectingBottomAction, setIsIntersectingBottomAction ] = useState(false);
  const showFloatingBottom = isIntersectingNote && !isIntersectingBottomAction;
  const noteRef = useRef();
  const bottomActionRef = useRef();

  useEffect(() => {
    if (noteRef.current) {
      const intersectionCallback = (entries) => {
        entries.forEach(entry => {
          setIsIntersectingNote(entry.isIntersecting);
        });
      };
      const intersectionObserverNote = new IntersectionObserver(intersectionCallback);
      intersectionObserverNote.observe(noteRef.current);
    }
  }, [noteRef.current]);

  useEffect(() => {
    if (bottomActionRef.current) {
      const intersectionCallback = (entries) => {
        entries.forEach(entry => {
          setIsIntersectingBottomAction(entry.isIntersecting);
        });
      };
      const intersectionObserverBottom = new IntersectionObserver(intersectionCallback);
      intersectionObserverBottom.observe(bottomActionRef.current);
    }
  }, [bottomActionRef.current]);

  const windowSize = useWindowSize();

  const toggleEditingFlights = useCallback(
    () => {
      setEditingFlights(!editingFlights);
    },
    [ editingFlights ],
  );

  const toggleEditingAccommodation = useCallback(
    () => {
      setEditingAccommodation(!editingAccommodation);
    },
    [ editingAccommodation ],
  );

  const finishEditing = useCallback(
    () => {
      setEditingFlights(false);
      setEditingAccommodation(false);
    },
    [],
  );

  useEffect(() => {
    scrollToTop(false);
  }, [ editingFlights, editingAccommodation ]);

  useEffect(() => {
    dispatch(
      setLayoutType(LAYOUT_TYPES.step)
    );

    dispatch(
      setCurrentStep(1)
    );

    return () => {
      dispatch(
        setLayoutType(LAYOUT_TYPES.default)
      );
    };
  }, []);

  useEffect(() => {
    if (editingFlights || editingAccommodation) {
      return;
    }

    const pcView = windowSize.width > 768;
    setIsPCView(pcView);
  }, [windowSize]);

  const { flight, hotel } = useSearchFlightSync(true, true, true);

  const { planDetail } = useSelector(selector);

  const { plans, fetching: fetchingPlanDetail } = planDetail;
  const {
    fetchingOutbound,
    fetchingInbound,
    searchedFlightsInput,
    selectedOutboundFlight,
    selectedInboundFlight,
    selectedOutboundDate,
    selectedInboundDate,
    outboundFlights,
    inboundFlights,
  } = flight;
  const fetchingFlight = fetchingOutbound || fetchingInbound;
  const { fetching: fetchingHotel, searchedHotelsInput, hotelsInput } = hotel;

  const tripStartDate = moment(selectedOutboundDate);

  useEffect(() => {
    if (editingAccommodation) {
      const formatedTripStartDate = tripStartDate.clone().format('YYYY-MM-DD');
      const _hotelsInput = planIndex === 1 ? {
        ...hotelsInput,
        hotelCode: plans.find(p => p.startDate === planStartDate).plan.hotel.id,
      } : {
        ...hotelsInput,
        provider: plans.find(p => p.startDate === formatedTripStartDate).plan.provider,
      };

      dispatch(
        fetchHotels(
          makeHotelsInput(_hotelsInput, searchedFlightsInput, planStartDate),
          SHOWING_HOTEL_LIMIT,
          1
        )
      );
    }
  }, [editingAccommodation]);

  if (!searchedFlightsInput || !searchedHotelsInput) {
    return null;
  }

  const { people } = searchedFlightsInput;

  const { totalPrice, pricePerAdult, pricePerChildrenA, pricePerChildrenB, pricePerChildrenC, pricePerChildren2, pricePerInfant } = usePrice(
    people, selectedOutboundFlight, selectedInboundFlight, plans.map(p => p.plan), selectedOutboundDate, 
  );

  let title = '申込み内容確認';
  if (editingFlights) {
    title = '航空便選択';
  }
  if (editingAccommodation) {
    const planStartDateDiplay = moment(planStartDate).format('YYYY年M月D日(ddd)');
    title = `宿泊内容変更 ${planIndex}日目 ${planStartDateDiplay}`;
  }

  const tripEndDate = moment(selectedInboundDate);
  const tripTotalDays = tripEndDate.diff(tripStartDate, 'days') + 1;

  let planEndDate = undefined;

  const schedule = new Array(tripTotalDays).fill(undefined).map((_, i) => {
    const date = tripStartDate.clone().add(i, 'days');

    const plan = getPlanFromPlansByDate(plans, date);

    if (plan) {
      planEndDate = date.clone().format('YYYY-MM-DD');
    }

    return {
      date,
      plan,
    };
  });

  const handleClickButton = async () => {
    setFetching(true);
    const sessionInput = makeCreateSessionInput(
      selectedOutboundFlight, selectedInboundFlight,
      selectedOutboundDate, selectedInboundDate,
      plans, searchedFlightsInput, searchedHotelsInput,
    );
    await createSession(sessionInput);
    setFetching(false);
    window.open(`${BASENAME}/login`, '_self');
  };

  const showSaveFlightsButton = !!(outboundFlights && outboundFlights.length && inboundFlights && inboundFlights.length);

  const { progressRate } = useProgress(fetchingOutbound, fetchingInbound, fetchingPlanDetail || fetchingHotel);

  const firstPlan = plans && plans[0] && plans[0].plan;

  return (
    <div className="pageConfirmation">
      <FData action="checkout" flightState={flight} plnaDetailState={planDetail} />
      <Loading
        show={fetchingFlight || fetchingHotel || fetchingPlanDetail || fetching}
        progressRate={progressRate}
        hideProgressBar={fetching}
      />
      <div className="subscriptionCheckWrapper">
        <div className="subscriptionCheckWrapperTitle">
          <LineTitleGray
            size="lg"
            linkTitle={editingAccommodation ? '変更せずに戻る' : undefined}
            handleLinkClick={finishEditing}
          >
            {title}
          </LineTitleGray>
        </div>
        <div className="table">
          {isPCView ? (
            <SubscriptionTable
              editingFlights={editingFlights}
              editingAccommodation={editingAccommodation}
              toggleEditingFlights={toggleEditingFlights}
              toggleEditingAccommodation={toggleEditingAccommodation}
              tripTotalDays={tripTotalDays}
              schedule={schedule}
              plans={plans}
              maxStayNights={maxStayNights}
              setMaxStayNights={setMaxStayNights}
              planStartDate={planStartDate}
              setPlanStartDate={setPlanStartDate}
              planEndDate={planEndDate}
              setPlanIndex={setPlanIndex}
              planIndex={planIndex}
            />
          ) : (
            <SubscriptionPlanContents
              editingAccommodation={editingAccommodation}
              toggleEditingFlights={toggleEditingFlights}
              toggleEditingAccommodation={toggleEditingAccommodation}
              tripTotalDays={tripTotalDays}
              schedule={schedule}
              plans={plans}
              maxStayNights={maxStayNights}
              setMaxStayNights={setMaxStayNights}
              planStartDate={planStartDate}
              setPlanStartDate={setPlanStartDate}
              planEndDate={planEndDate}
              planIndex={planIndex}
              setPlanIndex={setPlanIndex}
            />
          )}
          {!(editingFlights || editingAccommodation) && (
            <div style={{ marginTop: 32 }}>
              <SubscriptionPrice
                priceTotal={totalPrice}
                adultPrice={pricePerAdult}
                childrenAPrice={pricePerChildrenA}
                childrenBPrice={pricePerChildrenB}
                childrenCPrice={pricePerChildrenC}
                children2Price={pricePerChildren2}
                infantPrice={pricePerInfant}
                people={people}
              />
            </div>
          )}
          {!editingAccommodation && (
            <React.Fragment>
              <div className="subscriptionCheckActions">
                <div className="linkActions">
                  {!(editingFlights) ? (
                    <div className="linkMypage subscriptionCheckLinkWrapper">
                      <DataUpdateTimeDisplay
                        showUpdateNote
                        className="subscriptionCheckNote"
                      />
                      {firstPlan && (firstPlan.chargesRemarks || firstPlan.chargesInfantRemarks) && (
                        <div className="planRemarks">
                          {firstPlan.chargesRemarks && <p dangerouslySetInnerHTML={{ __html: firstPlan.chargesRemarks }} />}
                          {firstPlan.chargesInfantRemarks && (
                            <p dangerouslySetInnerHTML={{ __html: firstPlan.chargesInfantRemarks }} style={{ marginTop: 8 }} />
                          )}
                        </div>
                      )}
                      <LinkButton buttonType="cta" onClick={handleClickButton}>申込み手続きへ</LinkButton>
                    </div>
                  ) : (
                    <React.Fragment>
                      <div className="linkClose">
                        <LinkButton buttonType="navigate" onClick={finishEditing}>戻る</LinkButton>
                      </div>
                      {showSaveFlightsButton && (
                        <div className="linkMypage">
                          <LinkButton buttonType="cta" onClick={finishEditing}>内容確認へ</LinkButton>
                        </div>
                      )}
                    </React.Fragment>
                  )}
                </div>
              </div>
              {!editingFlights && (
                <div className="attentionText">【必ず「申し込み注意事項」をお読みいただいた上、お進みください】</div>
              )}
            </React.Fragment>
          )}
        </div>
      </div>
      {!(editingFlights || editingAccommodation) && (
        <React.Fragment>
          <NoteEntryConfirmation noteRef={noteRef} />

          <div style={{ marginTop: 60 }}>
            <SubscriptionPrice
              priceTotal={totalPrice}
              adultPrice={pricePerAdult}
              childrenAPrice={pricePerChildrenA}
              childrenBPrice={pricePerChildrenB}
              childrenCPrice={pricePerChildrenC}
              children2Price={pricePerChildren2}
              infantPrice={pricePerInfant}
              people={people}
            />
          </div>
          <div className="subscriptionCheckActions">
            <div className="linkActions">
              <div ref={bottomActionRef} className="linkMypage subscriptionCheckLinkWrapper">
                <DataUpdateTimeDisplay
                  showUpdateNote
                  className="subscriptionCheckNote"
                />
                {firstPlan && (firstPlan.chargesRemarks || firstPlan.chargesInfantRemarks) && (
                  <div className="planRemarks">
                    {firstPlan.chargesRemarks && <p dangerouslySetInnerHTML={{ __html: firstPlan.chargesRemarks }} />}
                    {firstPlan.chargesInfantRemarks && (
                      <p dangerouslySetInnerHTML={{ __html: firstPlan.chargesInfantRemarks }} style={{ marginTop: 8 }} />
                    )}
                  </div>
                )}
                <LinkButton buttonType="cta" onClick={handleClickButton} >申込み手続きへ</LinkButton>
              </div>
            </div>
          </div>
          <div className="attentionText">【必ず「申し込み注意事項」をお読みいただいた上、お進みください】</div>
          {(schedule && schedule.length && schedule[0].plan) && (
            <PlanningInfo className="mt-40" info={schedule[0].plan.planningImplementation} />
          )}
        </React.Fragment>
      )}
      {showFloatingBottom && (
        <div className="floatingBottom">
          <div className="linkActions">
            <LinkButton buttonType="cta" onClick={handleClickButton}>申込み手続きへ</LinkButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntryConfirmation;
